/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2018, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS 'AS IS' AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router-dom'
import { Query } from 'react-apollo'
import gql from 'graphql-tag'
import qs from 'query-string'
import moment from 'moment'

import {
  TabbedView,
  FilterSidebar,
  Picklist,
  PicklistWithData,
  DateRange,
  Input,
  Messages,
  Emails,
  CaseRelatedObjects,
  ButtonDropdown,
  Cases,
  CreateCaseModal,
  Modal,
  Button,
  SvgIcon,
  WhatsAppMessages,
  Search
} from '../components'

import {
  decodeBase64,
  hasDateChanged,
  normalizePhoneNumber
} from '../utils'

import {
  CONTENTTYPE_MESSAGE,
  CONTENTTYPE_EMAIL,
  CONTENTTYPE_WHATSAPPMESSAGE,
  IDENTITY_PICSAOS,
  IDENTITY_PICSA_ADVISORY_SERVICES
} from '../constants'


class Communications extends React.Component {
  static propTypes = {
    identityId: PropTypes.string,
    communicationType: PropTypes.oneOf(['Messages', 'Emails', 'WhatsApp']),
    fromAddr: PropTypes.string,
    toAddr: PropTypes.string,
    urlPath: PropTypes.string,
    title: PropTypes.string,
    noStatusFilter: PropTypes.bool
  }

  static defaultProps = {
    urlPath: '/os/communications',
    title: <React.Fragment>
        <SvgIcon
          variant='utility'
          symbol='comments'
          colour='light'
          size='large'
          className='slds-m-right_small'
        />
        <div className='slds-m-top_x-small u-font-size-x-large u-text-caps'>
          Communications
        </div>
      </React.Fragment>
  }

  state = {
    // shared filters
    communicationType: null,
    statusFilter: null,
    directionFilter: 'inbound',  // default
    scheduledAtFromFilter: null,
    scheduledAtToFilter: null,
    orderBy: this.props.communicationType === 'WhatsApp'
      ? '-created_at'
      : '-timestamp',
    messageTypeFilter: null,
    createdAtFromFilter: null,
    createdAtToFilter: null,
    unknownParticipantFilter: false,
    // message filters
    timestampFromFilter: null,
    timestampToFilter: null,
    fromAddrFilter: undefined,
    toAddrFilter: undefined,
    // email filters
    emailTypeFilter: null,
    // whatsapp filters
    searchString: '',
    searchFilter: '',
    // case creation
    productType: null,
    requestType: null,
    assignedTo: null,
    nextActionAt: undefined,
    priority: 'B',
    caseNote: '',
    followUp: false,
    selectedMessage: null,
    selectedMessageIdentity: null,
    affectsEmployer: null
  }

  setCaseCreateStateDefault = () => {
    let currentState = Object.assign({}, this.state)
    let updatedState = Object.assign(currentState, {
      openCase: false,
      attachToCase: false,
      productType: null,
      requestType: null,
      assignedTo: null,
      nextActionAt: undefined,
      priority: 'B',
      caseNote: '',
      followUp: false,
      selectedMessage: null,
      selectedMessageIdentity: null,
      affectsEmployer: null
    })
    return updatedState
  }

  componentDidMount = () => {
    // this life-cycle function implementation ensures that, on first render and/or
    // returning from another page, that filters will still be set to url and what it was before
    if (this.props.location.search) {
      this.setFiltersFromQueryString(this.props.location.search)
    } else {
      this.setQueryString(this.state, true)
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.location.search) {
      this.setFiltersFromQueryString(nextProps.location.search)
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    // NOTE: update query string without causing re-render
    let isAnyFilterSet = nextState.statusFilter ||
      nextState.directionFilter ||
      nextState.messageTypeFilter ||
      nextState.emailTypeFilter ||
      nextState.searchFilter ||
      nextState.createdAtFromFilter ||
      nextState.createdAtToFilter ||
      nextState.scheduledAtFromFilter ||
      nextState.scheduledAtToFilter ||
      nextState.timestampFromFilter ||
      nextState.timestampToFilter ||
      nextState.unknownParticipantFilter ||
      nextState.orderBy

    let hasAnyFilterChanged = this.state.communicationType !== nextState.communicationType ||
      this.state.statusFilter !== nextState.statusFilter ||
      this.state.directionFilter !== nextState.directionFilter ||
      this.state.messageTypeFilter !== nextState.messageTypeFilter ||
      this.state.emailTypeFilter !== nextState.emailTypeFilter ||
      this.state.searchFilter !== nextState.searchFilter ||
      hasDateChanged(this.state.createdAtFromFilter, nextState.createdAtFromFilter) ||
      hasDateChanged(this.state.createdAtToFilter, nextState.createdAtToFilter) ||
      hasDateChanged(this.state.scheduledAtFromFilter, nextState.scheduledAtFromFilter) ||
      hasDateChanged(this.state.scheduledAtToFilter, nextState.scheduledAtToFilter) ||
      hasDateChanged(this.state.timestampFromFilter, nextState.timestampFromFilter) ||
      hasDateChanged(this.state.timestampToFilter, nextState.timestampToFilter) ||
      this.state.unknownParticipantFilter !== nextState.unknownParticipantFilter ||
      this.state.orderBy !== nextState.orderBy

    if (this.props.location.search === '' && isAnyFilterSet) {
      this.setQueryString(nextState)
      return false
    }
    else if (Object.is(this.props.location.search, nextProps.location.search) && hasAnyFilterChanged) {
      this.setQueryString(nextState)
      return false
    }
    else if (hasAnyFilterChanged) {
      this.setQueryString(nextState)
      return false
    }

    return true
  }

  setFiltersFromQueryString = (queryString) => {
    let queryParams = qs.parse(queryString)

    let filters = Object.assign(
      {},
      ...Object.keys(queryParams).map(key => (  // ES6 way of assigning renamed object keys
        key === 'orderBy'
          ? { orderBy: queryParams[key] }
          : key === 'type'
            ? { communicationType: queryParams.type }
            : key === 'unknownParticipant'
              ? { unknownParticipantFilter: true }
              : [  'createdAtFrom', 'createdAtTo', 'scheduledAtFrom', 'scheduledAtTo', 'timestampFrom', 'timestampTo' ].indexOf(key) > -1
                ? { [`${key}Filter`]: moment(queryParams[key], 'YYYYMMDDTHHmm') }
                : { [`${key}Filter`]: queryParams[key] }
      ))
    )
    this.setState(filters)
  }

  setQueryString = (state, setDefault) => {
    let filters = {
      orderBy: state.orderBy
    }

    if (this.props.communicationType) {
      filters.type = this.props.communicationType
    } else {
      if (setDefault) {
        filters.type = 'Messages'
      } else if (state.communicationType) {
        filters.type = state.communicationType
      }
    }

    if (state.statusFilter) {
      filters.status = state.statusFilter
    }

    if (state.directionFilter) {
      filters.direction = state.directionFilter
    }

    if (state.messageTypeFilter) {
      filters.messageType = state.messageTypeFilter
    }

    if (state.emailTypeFilter) {
      filters.emailType = state.emailTypeFilter
    }

    if (state.searchFilter) {
      filters.searchFilter = state.searchFilter
    }

    if (state.fromAddrFilter) {
      filters.fromAddr = state.fromAddrFilter
    }

    if (state.toAddrFilter) {
      filters.toAddr = state.toAddrFilter
    }

    if (state.scheduledAtFromFilter) {
      filters.scheduledAtFrom = state.scheduledAtFromFilter.format('YYYYMMDDTHHmm')
    }
    if (state.scheduledAtToFilter) {
      filters.scheduledAtTo = state.scheduledAtToFilter.format('YYYYMMDDTHHmm')
    }

    if (setDefault && state.communicationType === 'Messages') {
      filters.timestampFrom = moment().subtract(7, 'days').format('YYYYMMDDTHHmm')
    } else if (state.timestampFromFilter) {
      filters.timestampFrom = state.timestampFromFilter.format('YYYYMMDDTHHmm')
    }
    if (state.timestampToFilter) {
      filters.timestampTo = state.timestampToFilter.format('YYYYMMDDTHHmm')
    }

    if (state.createdAtFromFilter) {
      filters.createdAtFrom = state.createdAtFromFilter.format('YYYYMMDDTHHmm')
    }
    if (state.createdAtToFilter) {
      filters.createdAtTo = state.createdAtToFilter.format('YYYYMMDDTHHmm')
    }

    if (state.unknownParticipantFilter) {
      filters.unknownParticipant = 'yes'
    }

    this.props.history.push({
      pathname: this.props.urlPath,
      search: qs.stringify(filters)
    })
  }

  changeDirectionFilter = (direction) => {
    if (this.state.communicationType === 'Whatsapp') {
      this.setState({
        directionFilter: direction,
        orderBy: '-created_at'
      })
    }

    if (direction === 'inbound') {
      this.setState({
        directionFilter: direction,
        orderBy: '-timestamp'
      })
    } else {
      this.setState({
        directionFilter: direction,
        orderBy: '-scheduled_at'
      })
    }
  }

  handleParticipantCategoryChoice = (category) => {
    this.setState({ unknownParticipantFilter: category === 'Unknown' })
  }

  changeOrderBy = (orderBy) => this.setState({ orderBy })

  getFilters = (communicationType) => {
    if (communicationType === 'Messages') {
      return this.getMessageFilters()
    }

    if (communicationType === 'Emails') {
      return this.getEmailFilters()
    }

    if (communicationType === 'WhatsApp') {
      return this.getWhatsAppMessageFilters()
    }

    return []
  }

  getMessageFilters = () => {
    let filters = [
      {
        title: 'Participant identity',
        options: <Picklist
          id='participantIdentity'
          options={['Known', 'Unknown']}
          default='Known'
          symbol='chevrondown'
          value={this.state.unknownParticipantFilter ? 'Unknown' : 'Known'}
          onChange={this.handleParticipantCategoryChoice}
          closeDropdownOnSelect={true}
          inline={true}
        />
      },
      {
        title: 'Status',
        options: <PicklistWithData
          id='status'
          forField='MessageStatus'
          default='All'
          symbol='chevrondown'
          value={this.state.statusFilter
            ? this.state.statusFilter
            : 'all'
          }
          onChange={status => this.setState({
            statusFilter: status === 'all' ? null : status
          })}
          closeDropdownOnSelect={true}
          inline={true}
        />
      },
      {
        title: 'Direction',
        options: <PicklistWithData
          id='direction'
          forField='MessageDirection'
          default='Inbound'
          symbol='chevrondown'
          value={this.state.directionFilter}
          onChange={this.changeDirectionFilter}
          closeDropdownOnSelect={true}
          inline={true}
          excludeOptions={this.props.identityId ? ['internal'] : null}
        />
      },
      {
        title: 'Message type',
        options: <PicklistWithData
          id='messageType'
          forField='MessageMessageType'
          default='All'
          symbol='chevrondown'
          value={this.state.messageTypeFilter
            ? this.state.messageTypeFilter
            : 'all'
          }
          onChange={messageType => this.setState({
            messageTypeFilter: messageType === 'all' ? null : messageType
          })}
          closeDropdownOnSelect={true}
          inline={true}
        />
      },
      {
        title: 'From number',
        options: <Input
          id='fromAddr'
          value={this.state.fromAddrFilter}
          onChange={e => this.setState({ fromAddrFilter: e.target.value})}
        />
      },
      {
        title: 'To number',
        options: <Input
          id='toAddr'
          value={this.state.toAddrFilter}
          onChange={e => this.setState({ toAddrFilter: e.target.value})}
        />
      },
      {
        title: 'Scheduled between',
        options: <DateRange
          startDate={this.state.scheduledAtFromFilter}
          endDate={this.state.scheduledAtToFilter}
          onStartDateChange={date => this.setState({ scheduledAtFromFilter: date })}
          onEndDateChange={date => this.setState({ scheduledAtToFilter: date })}
          startDatePlaceholder='From'
          endDatePlaceholder='To'
          maxDate={moment()}
          dateFormat='DD MMM YYYY, HH:mm'
          align='vertical'
          showTimeSelect={true}
          timeFormat='HH:mm'
        />
      },
      {
        title: 'Timestamp between',  // sent or received
        options: <DateRange
          startDate={this.state.timestampFromFilter}
          endDate={this.state.timestampToFilter}
          onStartDateChange={date => this.setState({ timestampFromFilter: date })}
          onEndDateChange={date => this.setState({ timestampToFilter: date })}
          startDatePlaceholder='From'
          endDatePlaceholder='To'
          maxDate={moment()}
          dateFormat='DD MMM YYYY, HH:mm'
          align='vertical'
          showTimeSelect={true}
          timeFormat='HH:mm'
        />
      }
    ]

    if (this.props.identityId) {
      filters.shift()
    }

    return filters
  }

  getEmailFilters = () => (
    [
      {
        title: 'Status',
        options: <PicklistWithData
          id='status'
          forField='EmailStatus'
          default='All'
          symbol='chevrondown'
          value={this.state.statusFilter
            ? this.state.statusFilter
            : 'all'
          }
          onChange={status => this.setState({
            statusFilter: status === 'all' ? null : status
          })}
          closeDropdownOnSelect={true}
          inline={true}
        />
      },
      {
        title: 'Direction',
        options: <PicklistWithData
          id='direction'
          forField='MessageDirection'
          default='Inbound'
          symbol='chevrondown'
          value={this.state.directionFilter}
          onChange={direction => this.setState({ directionFilter: direction })}
          closeDropdownOnSelect={true}
          inline={true}
          excludeOptions={this.props.identityId ? ['internal'] : null}
        />
      },
      {
        title: 'Email type',
        options: <PicklistWithData
          id='emailType'
          forField='EmailEmailType'
          default='All'
          symbol='chevrondown'
          value={this.state.emailTypeFilter
            ? this.state.emailTypeFilter
            : 'all'
          }
          onChange={emailType => this.setState({
            emailTypeFilter: emailType === 'all' ? null : emailType
          })}
          closeDropdownOnSelect={true}
          inline={true}
        />
      },
      {
        title: 'From email address',
        options: <Input
          id='fromAddr'
          value={this.state.fromAddrFilter || ''}
          onChange={e => this.setState({ fromAddrFilter: e.target.value})}
        />
      },
      {
        title: 'Scheduled between',
        options: <DateRange
          startDate={this.state.scheduledAtFromFilter}
          endDate={this.state.scheduledAtToFilter}
          onStartDateChange={date => this.setState({ scheduledAtFromFilter: date })}
          onEndDateChange={date => this.setState({ scheduledAtToFilter: date })}
          startDatePlaceholder='From'
          endDatePlaceholder='To'
          maxDate={moment()}
          dateFormat='DD MMM YYYY, HH:mm'
          align='vertical'
          showTimeSelect={true}
          timeFormat='HH:mm'
        />
      },
      {
        title: 'Created between',  // sent or received
        options: <DateRange
          startDate={this.state.createdAtFromFilter}
          endDate={this.state.createdAtToFilter}
          onStartDateChange={date => this.setState({ createdAtFromFilter: date })}
          onEndDateChange={date => this.setState({ createdAtToFilter: date })}
          startDatePlaceholder='From'
          endDatePlaceholder='To'
          maxDate={moment()}
          dateFormat='DD MMM YYYY, HH:mm'
          align='vertical'
          showTimeSelect={true}
          timeFormat='HH:mm'
        />
      }
    ]
  )

  getWhatsAppMessageFilters = () => {
    let filters = [
      {
        title: 'Participant identity',
        options: <Picklist
          id='participantIdentity'
          options={['Known', 'Unknown']}
          default='Known'
          symbol='chevrondown'
          value={this.state.unknownParticipantFilter ? 'Unknown' : 'Known'}
          onChange={this.handleParticipantCategoryChoice}
          closeDropdownOnSelect={true}
          inline={true}
        />
      },
      {
        title: 'Direction',
        options: <PicklistWithData
          id='direction'
          forField='WhatsappMessageDirection'
          default='Inbound'
          symbol='chevrondown'
          value={this.state.directionFilter || ''}
          onChange={direction => this.setState({ directionFilter: direction })}
          closeDropdownOnSelect={true}
          inline={true}
        />
      },
      {
        title: 'Status',
        options: <PicklistWithData
          id='status'
          forField='WhatsappMessageStatus'
          default='All'
          symbol='chevrondown'
          value={this.state.statusFilter || ''}
          onChange={status => this.setState({ statusFilter: status })}
          closeDropdownOnSelect={true}
          inline={true}
        />
      },
      {
        title: 'Message type',
        options: <PicklistWithData
          id='messageType'
          forField='WhatsappMessageMessageType'
          default='All'
          symbol='chevrondown'
          value={this.state.messageTypeFilter || ''}
          onChange={messageType => this.setState({ messageTypeFilter: messageType })}
          closeDropdownOnSelect={true}
          inline={true}
        />
      },
      {
        title: 'Created between',  // sent or received
        options: <DateRange
          startDate={this.state.createdAtFromFilter}
          endDate={this.state.createdAtToFilter}
          onStartDateChange={date => this.setState({ createdAtFromFilter: date })}
          onEndDateChange={date => this.setState({ createdAtToFilter: date })}
          startDatePlaceholder='From'
          endDatePlaceholder='To'
          maxDate={moment()}
          dateFormat='DD MMM YYYY, HH:mm'
          align='vertical'
          showTimeSelect={true}
          timeFormat='HH:mm'
        />
      }
    ]

    if (this.props.identityId) {
      filters.shift()
    } else {
      filters.unshift({
        title: 'Search',
        options: <Search
          id='search'
          value={this.state.searchString}
          placeholder='Enter string'
          onChange={e => this.setState({ searchString: e.target.value })}
          onClear={() => this.setState({ searchString: '' })}
          onClick={() => this.setState({ searchFilter: this.state.searchString })}
        />
      })
    }

    return filters
  }

  openOrAttachCase = (messageId, messageIdentityId) => {
    let options = [
      {
        label: 'Open case',
        onClick: () => this.setState({
          openCase: true,
          selectedMessage: messageId,
          selectedMessageIdentity: this.state.unknownParticipantFilter
            ? IDENTITY_PICSAOS
            : messageIdentityId
        })
      }
    ]

    if (this.state.unknownParticipantFilter === false) {
      options.push({
        label: 'Attach to existing case',
        onClick: () => this.setState({
          attachToCase: true,
          selectedMessage: messageId,
          selectedMessageIdentity: messageIdentityId
        })
      })
    }

    return (
      <CaseRelatedObjects
        objectId={messageId}
        noObjectFoundDisplay={
          <ButtonDropdown
            id={`buttonDropDown_${messageId}`}
            label='Actions'
            options={options}
            stopPropagation={true}
            minWidth={110}
          />
        }
        showCaseStatus={true}
      />
    )
  }

  displayListing = (communicationType) => {
    const { fromAddrFilter, toAddrFilter, statusFilter, directionFilter, messageTypeFilter, orderBy,
      scheduledAtFromFilter, scheduledAtToFilter, timestampFromFilter, timestampToFilter, emailType,
      createdAtFromFilter, createdAtToFilter, unknownParticipantFilter, searchFilter} = this.state

    switch(communicationType) {
      case 'Messages':
        let fromAddr = fromAddrFilter
        let toAddr = toAddrFilter

        return (
          <Messages
            status={statusFilter}
            direction={directionFilter}
            messageType={messageTypeFilter}
            fromAddr={fromAddr ? normalizePhoneNumber(fromAddr) : null}
            toAddr={toAddr ? normalizePhoneNumber(toAddr) : null}
            // set from/to identity id props according to direction; identityId gets passed into
            // Communications component when we want to set identity as an unchangeable filter
            fromIdentityId={
              directionFilter === "inbound"
                ? this.props.identityId
                : IDENTITY_PICSA_ADVISORY_SERVICES
            }
            toIdentityId={
              directionFilter === "inbound"
                ? IDENTITY_PICSA_ADVISORY_SERVICES
                : this.props.identityId
            }
            scheduledAtFrom={
              scheduledAtFromFilter
                ? moment
                    .utc(scheduledAtFromFilter)
                    .format("YYYY-MM-DD HH:mm:ss.SSS")
                : null
            }
            scheduledAtTo={
              scheduledAtToFilter
                ? moment
                    .utc(scheduledAtToFilter)
                    .format("YYYY-MM-DD HH:mm:ss.SSS")
                : null
            }
            timestampFrom={
              timestampFromFilter
                ? moment
                    .utc(timestampFromFilter)
                    .format("YYYY-MM-DD HH:mm:ss.SSS")
                : null
            }
            timestampTo={
              timestampToFilter
                ? moment
                    .utc(timestampToFilter)
                    .format("YYYY-MM-DD HH:mm:ss.SSS")
                : null
            }
            unknownParticipant={unknownParticipantFilter}
            orderBy={orderBy}
            changeSort={this.changeOrderBy}
            actionColumn={{
              name: "Related case",
              render: this.openOrAttachCase,
            }}
          />
        );

      case 'Emails':
        return (
          <Emails
            status={statusFilter}
            direction={directionFilter}
            emailType={emailType}
            fromAddr={fromAddrFilter}
            fromIdentityId={
              directionFilter === "inbound" ? this.props.identityId : null
            }
            toIdentityId={
              directionFilter === "outbound" ? this.props.identityId : null
            }
            scheduledAtFrom={
              scheduledAtFromFilter
                ? moment
                    .utc(scheduledAtFromFilter)
                    .format("YYYY-MM-DD HH:mm:ss.SSS")
                : null
            }
            scheduledAtTo={
              scheduledAtToFilter
                ? moment
                    .utc(scheduledAtToFilter)
                    .format("YYYY-MM-DD HH:mm:ss.SSS")
                : null
            }
            createdAtFrom={
              createdAtFromFilter
                ? moment
                    .utc(createdAtFromFilter)
                    .format("YYYY-MM-DD HH:mm:ss.SSS")
                : null
            }
            createdAtTo={
              createdAtToFilter
                ? moment
                    .utc(createdAtToFilter)
                    .format("YYYY-MM-DD HH:mm:ss.SSS")
                : null
            }
            orderBy={orderBy}
            changeSort={this.changeOrderBy}
            actionColumn={{
              name: "Related case",
              render: this.openOrAttachCase,
            }}
          />
        );
      case 'WhatsApp':
        return (
          <WhatsAppMessages
            fromIdentityId={
              directionFilter === "inbound" ? this.props.identityId : null
            }
            toIdentityId={
              directionFilter === "inbound" ? null : this.props.identityId
            }
            direction={directionFilter}
            status={statusFilter}
            messageType={messageTypeFilter}
            createdAtStartDate={
              createdAtFromFilter
                ? moment
                    .utc(createdAtFromFilter)
                    .format("YYYY-MM-DD HH:mm:ss.SSS")
                : null
            }
            createdAtEndDate={
              createdAtToFilter
                ? moment
                    .utc(createdAtToFilter)
                    .format("YYYY-MM-DD HH:mm:ss.SSS")
                : null
            }
            unknownParticipant={unknownParticipantFilter}
            search={searchFilter}
            orderBy={orderBy}
            changeSort={this.changeOrderBy}
            actionColumn={{
              name: "Related case",
              render: this.openOrAttachCase,
            }}
          />
        );

      default:
        return null
    }
  }

  saveCaseNote = (caseId, currentUser) => {
    let input = {
      case: caseId,
      note: this.state.caseNote,
      author: currentUser,
      direction: "internal"
    }

    this.props.saveCaseNote({ variables: { input } })
    .then(() => {
      console.log("Case note saved successfully")
      this.setState({ caseNote: '' })
      this.createCaseRelation(caseId)
    })
    .catch((error) => {
      console.log('Case note save failed!', error)
      this.setState({ caseNote: '', openCase: false })
    })
  }

    createCaseRelation = (caseId) => {
    let input = {
      case: caseId,
      contentType: CONTENTTYPE_MESSAGE,
      objectId: decodeBase64(this.state.selectedMessage)
    }

    this.props.saveCaseRelation({ variables: { input } })
    .then(() => {
      console.log('Case relation saved successfully')
      this.setState({ openCase: false })
    })
    .catch(error => {
      console.log('Failed to save case relation', error)
      this.setState({ openCase: false })
    })
  }

  getAssociatedContentType = (communicationType) => {
    if (communicationType === 'Messages') {
      return CONTENTTYPE_MESSAGE
    }

    if (communicationType === 'Emails') {
      return CONTENTTYPE_EMAIL
    }

    if (communicationType === 'WhatsApp') {
      return CONTENTTYPE_WHATSAPPMESSAGE
    }

    return null
  }

  render() {
    let tabs = ['Messages', 'Emails', 'WhatsApp']
    let filters = this.getFilters(this.state.communicationType)

    if (this.state.openCase) {
      if (this.state.unknownParticipantFilter) {
        return (
          <Query
            query={
              gql`
                query($description: String!) {
                  identities(responsibilities_Description: $description, first: 1) {
                    edges {
                      node {
                        id
                      }
                    }
                  }
                }
              `
            }
            variables={{ description: 'lead_agent_unknown' }}>

            {({ loading, error, data }) => {
              if (loading || error) {
                return null
              }

              return (
                <CreateCaseModal
                  affects={this.state.selectedMessageIdentity}
                  assignedTo={data.identities.edges[0].node.id}
                  productType='other'
                  requestType='Unknown Sender'
                  followUp={true}
                  onSaveClick={() => this.setState({ openCase: false })}
                  onCloseClick={() => this.setState({ openCase: false })}
                  caseRelations={[
                    {
                      objectId: decodeBase64(this.state.selectedMessage),
                      contentType: this.getAssociatedContentType(this.state.communicationType)
                    }
                  ]}
                />
              )
            }}
          </Query>
        )
      } else {
        return (
          <CreateCaseModal
            affects={this.state.selectedMessageIdentity}
            onSaveClick={() => this.setState({ openCase: false })}
            onCloseClick={() => this.setState({ openCase: false })}
            caseRelations={[
              {
                objectId: decodeBase64(this.state.selectedMessage),
                contentType: this.getAssociatedContentType(this.state.communicationType)
              }
            ]}
          />
        )
      }
    }

    if (this.state.attachToCase) {
      return (
        <Modal
          body={
            <Cases
              status=''
              productType=''
              requestType=''
              priority=''
              assignedTo=''
              createdBy=''
              search=''
              reviewed=''
              affects={this.state.selectedMessageIdentity}
              showStatusColumn={true}
              actionColumnRender={caseId => (
                <Button
                    onClick={() => this.createCaseRelation(caseId)}>
                  Attach
                </Button>
              )}
              condensedView={true}
            />
          }
          sizeLarge={true}
          closeButton={true}
          onCloseClick={() => this.setState({ attachToCase: false })}
        />
      )
    }

    return (
      <div className='slds-grow slds-scrollable_y' role="main">
        <div className='slds-grid slds-wrap slds-size_1-of-1'>
          <div className='tab-nav slds-size_1-of-1 u-bg-color-white product-listing-page-header'>
            {this.props.title !== null &&
              <div className='slds-m-top_small slds-p-left_small product-listing-page-header_title'>
                {this.props.title}
              </div>
            }
            {this.props.noStatusFilter
              ? null
              : <TabbedView
                  tabs={tabs}
                  contents={[]}
                  selectedTab={tabs.indexOf(this.state.communicationType)}
                  onClick={tabIndex => this.setState(prevState => {
                    let communicationType = tabs[tabIndex]
                    if (prevState.communicationType !== communicationType) {
                      return {
                        // shared filters
                        communicationType,
                        statusFilter: null,
                        directionFilter: 'inbound',
                        scheduledAtFromFilter: null,
                        scheduledAtToFilter: null,
                        fromAddrFilter: null,
                        orderBy: communicationType === 'Messages'
                          ? '-timestamp'
                          : '-created_at',
                        // message filters
                        messageTypeFilter: null,
                        timestampFromFilter: null,
                        timestampToFilter: null,
                        toAddrFilter: null,
                        // email filters
                        emailTypeFilter: null,
                        createdAtFromFilter: null,
                        createdAtToFilter: null,
                        // from/to identity
                        // from/to addr
                        // has attachments?
                      }
                    }
                  })}
                  tabsClassName='slds-align_absolute-center product-listing-page-header_tabs'
                />
            }
          </div>
          <FilterSidebar
            filters={filters}
            filtersApplied={[
                this.state.statusFilter || '',
                this.state.directionFilter || '',
                this.state.messageTypeFilter || '',
                this.state.emailTypeFilter || '',
                this.state.scheduledAtFromFilter || this.state.scheduledAtToFilter
                  ? this.state.scheduledAtFromFilter
                    ? this.state.scheduledAtFromFilter.toString()
                    : this.state.scheduledAtToFilter.toString()
                  : '',
                this.state.timestampFromFilter || this.state.timestampToFilter
                  ? this.state.timestampFromFilter
                    ? this.state.timestampFromFilter.toString()
                    : this.state.timestampToFilter.toString()
                  : '',
                this.state.createdAtFromFilter || this.state.createdAtToFilter
                  ? this.state.createdAtFromFilter
                    ? this.state.createdAtFromFilter.toString()
                    : this.state.createdAtToFilter.toString()
                  : '',
              ].filter(filter => filter.length > 0).length
            }
            onClear={() => this.setState({
              statusFilter: null,
              directionFilter: 'inbound',
              scheduledAtFromFilter: null,
              scheduledAtToFilter: null,
              messageTypeFilter: null,
              fromAddrFilter: undefined,
              toAddrFilter: undefined,
              timestampFromFilter: null,
              timestampToFilter: null,
              emailTypeFilter: null,
              createdAtFromFilter: null,
              createdAtToFilter: null
            })}
            key={filters}
          />
          <div className='slds-col slds-size_10-of-12 slds-scrollable_x slds-border_top'>
            {/* consider this.props.communicationType above this.state.communicationType when status filter
            value gets determined and passed into this component instead of relying on internal tabbed view */}
            {this.displayListing(this.props.communicationType || this.state.communicationType)}
          </div>
        </div>
      </div>
    )
  }
}

export default withRouter(Communications)



// WEBPACK FOOTER //
// ./src/containers/Communications.js